import { z } from "zod";
import { crmYesNo, filteredAgencyTypes } from "./agencySchemas";
import { effectiveCrmProjectIdForWizard } from "./registrationEventCampaign";
import type { RegistrationAccountResumeValues, RegistrationAccountValues } from "./schema";
import { defaultJurisdictionRow } from "./geoData";
import { isErStakeholderType, needsOrganizationStep } from "./stakeholderSteps";
import { STAKEHOLDER_COMMANDER, STAKEHOLDER_TIER_II } from "./stakeholderConstants";
import { DISPATCH_API_KEYS } from "./dispatchConstants";
import { registrationDispatchDefaults } from "./dispatchSchemas";
import { needsDispatchCapabilitiesStep, needsMutualAidStep } from "./stepEngine";
import {
  isTrainerJobTitle,
  resolveRegistrationStakeholderType,
  resolveRegistrationWizardStakeholderType,
} from "./roleMap";
import { showOrganizationDepartmentField } from "./organizationFields";

export const REGISTRATION_STATUS_PENDING = "Pending";
export const REGISTRATION_STATUS_ACTIVE = "Active";

export const DEFAULT_REGISTRATION_COUNTRY = "United States";

/** One jurisdiction row (legacy allows up to five). */
export type RegistrationJurisdictionRow = {
  country: string;
  state: string;
  county: string;
};

/** Fields collected after the ABP account step (mailing → jurisdictions → organization in buxus-apps). */
export type RegistrationExtendedFormValues = {
  jurisdictions: RegistrationJurisdictionRow[];
  mailingAddress1: string;
  mailingAddress2: string;
  mailingCity: string;
  mailingState: string;
  mailingCounty: string;
  mailingZip: string;
  mailingCountry: string;
  situsAddress1: string;
  situsAddress2: string;
  situsCity: string;
  situsState: string;
  situsCounty: string;
  situsZip: string;
  situsCountry: string;
  institution: string;
  organizationType: string;
  department: string;
  institutionPhone: string;
  typeOfAgency: string[];
  stationNameOrNumber: string;
  nameOfAgencyCommandingOfficer: string;
  nonEmergencyNumberForAgencyOrDepartment: string;
  website: string;
  staffed: string;
  numberOfPeopleInDepartment: string;
  canYourDepartmentProvide: string[];
  /** @deprecated Retired from registration — historic CRM Yes/No only (Provide_medical_assistance). */
  provideMedicalAssistance: boolean;
  /** @deprecated Retired from registration — historic CRM Yes/No only (Assist_with_directing_traffic). */
  assistInDirectingTraffic: boolean;
  /** @deprecated Retired from registration — historic CRM Yes/No only (Radio_communication_with_other_responding_agencies). */
  radioCommunication: boolean;
  /** @deprecated Retired from registration — historic CRM Yes/No only (Have_800_Trunk_radios); do not add "800 Trunk radios" picklist option. */
  trunkRadios: boolean;
  /** @deprecated Retired from registration — historic CRM Yes/No only (Computers_or_mobile_on_scene_internet_access). */
  computerInternetAccess: boolean;
  naturalGasEmergencies: boolean;
  hazardousLiquidContingencyPlan: boolean;
  publicEvacuation: boolean;
  hazwoper: string;
  naturalGasNfpa329: boolean;
  hazardousMaterials: boolean;
  hazardousLiquids: boolean;
  highlyVolatileLiquids: boolean;
  pipelineEmergencies: boolean;
  incidentCommand: boolean;
  nationalIncidentManagementSystem: boolean;
  dotEmergencyResponseGuide: boolean;
  emergencyMedicalTechnician: boolean;
  mutualAidAgreements: boolean;
  withWhom: string;
  howDidYouFindOutAboutBuxus: string;
  howDidYouFindOutAboutBuxusAgency: string;
  howDidYouFindOutAboutBuxusAssociation: string;
  howDidYouFindOutAboutBuxusConference: string;
  howDidYouFindOutAboutBuxusOperator: string;
  howDidYouFindOutAboutBuxusTrainer: string;
  howDidYouFindOutAboutBuxusWebinar: string;
  dispatchFacilityClassification: string;
  dispatchDisciplinesServedByAgency: string[];
  dispatchMainAreaOfResponsibility: string;
  dispatchEstPopulationOfResponsibilityArea: string;
  dispatchComputerAidedDispatchSystemUsed: string;
  dispatchComputerAidedDispatchSystemUsedOther: string;
  dispatchCanDetermineCallerLatLongGis: boolean | null;
  dispatchProgramsUsedByAgency: string[];
  dispatchRadioSystemsUsedByAgency: string[];
  dispatchRadioSystemsUsedByAgencyOther: string;
  dispatchPublicNotificationSystemUsed: string;
  dispatchPublicNotificationSystemUsedOther: string;
  dispatchIsContinuingEducationRequired: string;
  dispatchHasAccessToEmergencyTopics: string[];
  dispatchSopSogForEmergencyTopics: string[];
  dispatchTrainingCoversEmergencyTopics: string[];
  dispatchCompletedCoursesOnEmergencyTopics: string[];
  dispatchIsRadioSystemEncrypted: boolean | null;
  dispatchIsRadioSystemP25Compliant: boolean | null;
  dispatchDoesStateRequireCertification: string;
  dispatchDoesStateRequireCertificationOther: string;
};

export const registrationExtendedDefaults = (): RegistrationExtendedFormValues => ({
  jurisdictions: [defaultJurisdictionRow()],
  mailingAddress1: "",
  mailingAddress2: "",
  mailingCity: "",
  mailingState: "",
  mailingCounty: "",
  mailingZip: "",
  mailingCountry: DEFAULT_REGISTRATION_COUNTRY,
  situsAddress1: "",
  situsAddress2: "",
  situsCity: "",
  situsState: "",
  situsCounty: "",
  situsZip: "",
  situsCountry: "",
  institution: "",
  organizationType: "",
  department: "",
  institutionPhone: "",
  typeOfAgency: [],
  stationNameOrNumber: "",
  nameOfAgencyCommandingOfficer: "",
  nonEmergencyNumberForAgencyOrDepartment: "",
  website: "",
  staffed: "",
  numberOfPeopleInDepartment: "",
  canYourDepartmentProvide: [],
  provideMedicalAssistance: false,
  assistInDirectingTraffic: false,
  radioCommunication: false,
  trunkRadios: false,
  computerInternetAccess: false,
  naturalGasEmergencies: false,
  hazardousLiquidContingencyPlan: false,
  publicEvacuation: false,
  hazwoper: "",
  naturalGasNfpa329: false,
  hazardousMaterials: false,
  hazardousLiquids: false,
  highlyVolatileLiquids: false,
  pipelineEmergencies: false,
  incidentCommand: false,
  nationalIncidentManagementSystem: false,
  dotEmergencyResponseGuide: false,
  emergencyMedicalTechnician: false,
  mutualAidAgreements: false,
  withWhom: "",
  howDidYouFindOutAboutBuxus: "",
  howDidYouFindOutAboutBuxusAgency: "",
  howDidYouFindOutAboutBuxusAssociation: "",
  howDidYouFindOutAboutBuxusConference: "",
  howDidYouFindOutAboutBuxusOperator: "",
  howDidYouFindOutAboutBuxusTrainer: "",
  howDidYouFindOutAboutBuxusWebinar: "",
  ...registrationDispatchDefaults(),
});

const jurisdictionRowSchema = z.object({
  country: z.string().trim().min(1, "Country is required"),
  state: z.string().trim().min(1, "State / Province is required"),
  county: z.string().trim().min(1, "County is required"),
});

export const registrationJurisdictionStepSchema = z
  .object({
    jurisdictions: z
      .array(jurisdictionRowSchema)
      .min(1, "Add at least one jurisdiction")
      .max(5, "You may add up to five jurisdictions"),
  })
  .superRefine((v, ctx) => {
    const key = (row: RegistrationJurisdictionRow) =>
      `${row.country.trim()}|${row.state.trim()}|${row.county.trim()}`.toLowerCase();
    v.jurisdictions.forEach((row, i) => {
      const c = row.country.trim();
      const s = row.state.trim();
      const co = row.county.trim();
      if (!c || !s || !co) return;
      const k = key(row);
      for (let j = 0; j < i; j++) {
        const prev = v.jurisdictions[j];
        if (prev.country.trim() && prev.state.trim() && prev.county.trim() && key(prev) === k) {
          ctx.addIssue({
            code: "custom",
            path: ["jurisdictions", i, "county"],
            message: "This jurisdiction is already listed. Choose a different county or remove the duplicate row.",
          });
          break;
        }
      }
    });
  });

export const registrationMailingStepSchema = z.object({
  mailingAddress1: z.string().trim().min(1, "Address line 1 is required"),
  mailingAddress2: z.string().trim().optional().default(""),
  mailingCity: z.string().trim().min(1, "City is required"),
  mailingState: z.string().trim().min(1, "State / Province is required"),
  mailingCounty: z.string().trim().min(1, "County is required"),
  mailingZip: z.string().trim().min(1, "Zip or postal code is required"),
  mailingCountry: z.string().trim().min(1, "Country is required"),
});

/**
 * Body for `POST /api/buxus/account-setup-crm` (same shape as legacy `NewRegistrationDto`).
 * `status` is intentionally omitted — backend computes authoritative Status on submit.
 */
/** Account fields sent to CRM (passwords are not part of the CRM payload). */
export type RegistrationAccountCrmFields = Pick<
  RegistrationAccountValues,
  | "stakeholderType"
  | "jobTitle"
  | "isUnaffiliatedProfessionalErTrainer"
  | "firstName"
  | "lastName"
  | "phone"
  | "email"
>;

function putCrmString(
  base: Record<string, unknown>,
  key: string,
  value: string | undefined,
): void {
  const trimmed = value?.trim();
  if (trimmed) base[key] = trimmed;
}

export function buildAccountSetupCrmPayload(input: {
  abpUserId: string;
  account: RegistrationAccountValues | RegistrationAccountResumeValues;
  extended: RegistrationExtendedFormValues;
  quickCode: string;
  approvalCode?: string | null;
  eventCodeSlug?: string | null;
  stagedCrmProjectId?: string | null;
  /** Express return / QC resume: omit blank identity fields so Zoho upsert keeps CRM values. */
  omitEmptyAccountFields?: boolean;
}): Record<string, unknown> {
  const {
    abpUserId,
    account,
    extended,
    quickCode,
    approvalCode,
    eventCodeSlug,
    stagedCrmProjectId,
    omitEmptyAccountFields = false,
  } = input;
  const qc = quickCode.trim();
  const projectId = effectiveCrmProjectIdForWizard(
    stagedCrmProjectId ?? undefined,
    eventCodeSlug ?? undefined,
  );
  const crmStakeholderType = resolveRegistrationStakeholderType(
    account.stakeholderType,
    account.jobTitle,
    account.isUnaffiliatedProfessionalErTrainer,
  );
  const wizardStakeholderType = resolveRegistrationWizardStakeholderType(
    account.stakeholderType,
    account.jobTitle,
  );
  const trainerJobTitle = isTrainerJobTitle(account.jobTitle);

  const base: Record<string, unknown> = {
    abpUserId,
    stakeholder_Type: crmStakeholderType,
    jurisdictions: extended.jurisdictions
      .filter((j) => j.country.trim() && j.state.trim() && j.county.trim())
      .map((j) => ({
        country: j.country.trim(),
        state: j.state.trim(),
        county: j.county.trim(),
      })),
    mailing_Address_1: extended.mailingAddress1.trim(),
    mailing_Address_2: extended.mailingAddress2.trim() || undefined,
    mailing_City: extended.mailingCity.trim(),
    mailing_State_Normalized: extended.mailingState.trim(),
    mailing_County: extended.mailingCounty.trim(),
    mailing_Zip: extended.mailingZip.trim(),
    mailing_Country: extended.mailingCountry.trim(),
    email_Opt_Out: false,
    project_ID_2: projectId,
    approval_Code: approvalCode?.trim() || undefined,
  };

  const assignAccountString = (crmKey: string, value: string) => {
    if (omitEmptyAccountFields) {
      putCrmString(base, crmKey, value);
      return;
    }
    base[crmKey] = value.trim();
  };

  assignAccountString("first_Name", account.firstName);
  assignAccountString("last_Name", account.lastName);
  assignAccountString("email", account.email);
  assignAccountString("phone", account.phone);
  assignAccountString("job_Title", account.jobTitle);
  assignAccountString(
    "is_Unaffiliated_Professional_ER_Trainer",
    account.isUnaffiliatedProfessionalErTrainer,
  );
  assignAccountString("communications_Role", account.communicationsRole);
  assignAccountString("tenant_Email", account.email);
  assignAccountString("tenant_Phone", account.phone);

  if (qc.length > 4) base.quick_Code_Computed = qc;

  const first = account.firstName.trim();
  const last = account.lastName.trim();
  if (first && last) base.full_Name = `${first} ${last}`;

  putCrmString(base, "situs_Address_1", extended.situsAddress1);
  putCrmString(base, "situs_Address_2", extended.situsAddress2);
  putCrmString(base, "situs_City", extended.situsCity);
  putCrmString(base, "situs_State", extended.situsState);
  putCrmString(base, "situs_County", extended.situsCounty);
  putCrmString(base, "situs_Zip", extended.situsZip);
  putCrmString(base, "situs_Country", extended.situsCountry);

  if (needsOrganizationStep(wizardStakeholderType)) {
    base.institution = extended.institution.trim();
    base.organization_Type = extended.organizationType.trim();
    base.department = showOrganizationDepartmentField(wizardStakeholderType, account.jobTitle)
      ? extended.department.trim()
      : "";
    base.institution_Phone = extended.institutionPhone.trim();
  }

  if (isErStakeholderType(wizardStakeholderType) && !trainerJobTitle) {
    const agencyTypes = filteredAgencyTypes(extended.typeOfAgency);
    if (agencyTypes.length > 0) base.type_of_Agency = agencyTypes;
    if (extended.stationNameOrNumber.trim()) {
      base.station_Name_or_Number = extended.stationNameOrNumber.trim();
    }
    if (extended.nameOfAgencyCommandingOfficer.trim()) {
      base.name_of_Agency_Commanding_Officer = extended.nameOfAgencyCommandingOfficer.trim();
    }
    if (extended.nonEmergencyNumberForAgencyOrDepartment.trim()) {
      base.non_Emergency_Number_for_Agency_or_Department =
        extended.nonEmergencyNumberForAgencyOrDepartment.trim();
    }
    if (extended.website.trim()) base.website = extended.website.trim();
    if (extended.staffed.trim() && extended.staffed.trim() !== "-1") {
      base.staffed = extended.staffed.trim();
    }
    if (extended.numberOfPeopleInDepartment.trim()) {
      base.number_of_people_in_department = extended.numberOfPeopleInDepartment.trim();
    }
  }

  const isCommanderTier =
    wizardStakeholderType === STAKEHOLDER_COMMANDER || wizardStakeholderType === STAKEHOLDER_TIER_II;

  if (isCommanderTier && !trainerJobTitle) {
    if (extended.canYourDepartmentProvide.length > 0) {
      base.can_your_department_provide = extended.canYourDepartmentProvide;
    }
    // Stop writing retired capability Yes/Nos (Provide_medical_assistance, etc.) — historic CRM only.
    base.natural_gas_emergencies = crmYesNo(extended.naturalGasEmergencies);
    base.hazardous_liquid_contingency_plan = crmYesNo(extended.hazardousLiquidContingencyPlan);
    base.public_evacuation = crmYesNo(extended.publicEvacuation);
    if (extended.hazwoper.trim() && extended.hazwoper.trim() !== "-1") {
      base.hazwoper = extended.hazwoper.trim();
    }
    base.natural_Gas_NFPA_329 = crmYesNo(extended.naturalGasNfpa329);
    base.hazardous_Materials_Weapons_of_Mass_Destruction = crmYesNo(extended.hazardousMaterials);
    base.hazardous_liquids = crmYesNo(extended.hazardousLiquids);
    base.highly_volatile_liquids = crmYesNo(extended.highlyVolatileLiquids);
    base.pipeline_Emergencies = crmYesNo(extended.pipelineEmergencies);
    base.incident_Command = crmYesNo(extended.incidentCommand);
    base.national_Incident_Management_System = crmYesNo(extended.nationalIncidentManagementSystem);
    base.doT_Emergency_Response_Guide = crmYesNo(extended.dotEmergencyResponseGuide);
    base.emergency_Medical_Technician = crmYesNo(extended.emergencyMedicalTechnician);
  }

  if (
    needsDispatchCapabilitiesStep({
      stakeholderType: wizardStakeholderType,
      jobTitle: account.jobTitle.trim(),
      typeOfAgency: extended.typeOfAgency,
    })
  ) {
    const k = DISPATCH_API_KEYS;
    putCrmString(base, k.facilityClassification, extended.dispatchFacilityClassification);
    if (extended.dispatchDisciplinesServedByAgency.length > 0) {
      base[k.disciplinesServedByAgency] = extended.dispatchDisciplinesServedByAgency;
    }
    putCrmString(base, k.mainAreaOfResponsibility, extended.dispatchMainAreaOfResponsibility);
    putCrmString(
      base,
      k.estPopulationOfResponsibilityArea,
      extended.dispatchEstPopulationOfResponsibilityArea,
    );
    putCrmString(
      base,
      k.computerAidedDispatchSystemUsed,
      extended.dispatchComputerAidedDispatchSystemUsed,
    );
    if (extended.dispatchComputerAidedDispatchSystemUsed.trim() === "Other") {
      putCrmString(
        base,
        k.computerAidedDispatchSystemUsedOther,
        extended.dispatchComputerAidedDispatchSystemUsedOther,
      );
    }
    if (extended.dispatchCanDetermineCallerLatLongGis !== null) {
      base[k.canDetermineCallerLatLongGis] = crmYesNo(extended.dispatchCanDetermineCallerLatLongGis);
    }
    if (extended.dispatchProgramsUsedByAgency.length > 0) {
      base[k.programsUsedByAgency] = extended.dispatchProgramsUsedByAgency;
    }
    if (extended.dispatchRadioSystemsUsedByAgency.length > 0) {
      base[k.radioSystemsUsedByAgency] = extended.dispatchRadioSystemsUsedByAgency;
    }
    if (extended.dispatchRadioSystemsUsedByAgency.includes("Other")) {
      putCrmString(
        base,
        k.radioSystemsUsedByAgencyOther,
        extended.dispatchRadioSystemsUsedByAgencyOther,
      );
    }
    putCrmString(base, k.publicNotificationSystemUsed, extended.dispatchPublicNotificationSystemUsed);
    if (extended.dispatchPublicNotificationSystemUsed.trim() === "Other") {
      putCrmString(
        base,
        k.publicNotificationSystemUsedOther,
        extended.dispatchPublicNotificationSystemUsedOther,
      );
    }
    putCrmString(base, k.isContinuingEducationRequired, extended.dispatchIsContinuingEducationRequired);
    if (extended.dispatchHasAccessToEmergencyTopics.length > 0) {
      base[k.hasAccessToEmergencyTopics] = extended.dispatchHasAccessToEmergencyTopics;
    }
    if (extended.dispatchSopSogForEmergencyTopics.length > 0) {
      base[k.sopSogForEmergencyTopics] = extended.dispatchSopSogForEmergencyTopics;
    }
    if (extended.dispatchTrainingCoversEmergencyTopics.length > 0) {
      base[k.trainingCoversEmergencyTopics] = extended.dispatchTrainingCoversEmergencyTopics;
    }
    if (extended.dispatchCompletedCoursesOnEmergencyTopics.length > 0) {
      base[k.completedCoursesOnEmergencyTopics] = extended.dispatchCompletedCoursesOnEmergencyTopics;
    }
    if (extended.dispatchIsRadioSystemEncrypted !== null) {
      base[k.isRadioSystemEncrypted] = crmYesNo(extended.dispatchIsRadioSystemEncrypted);
    }
    if (extended.dispatchIsRadioSystemP25Compliant !== null) {
      base[k.isRadioSystemP25Compliant] = crmYesNo(extended.dispatchIsRadioSystemP25Compliant);
    }
    putCrmString(base, k.doesStateRequireCertification, extended.dispatchDoesStateRequireCertification);
    if (extended.dispatchDoesStateRequireCertification.trim() === "Other") {
      putCrmString(
        base,
        k.doesStateRequireCertificationOther,
        extended.dispatchDoesStateRequireCertificationOther,
      );
    }
  }

  if (
    needsMutualAidStep({
      stakeholderType: wizardStakeholderType,
      jobTitle: account.jobTitle.trim(),
      typeOfAgency: extended.typeOfAgency,
    })
  ) {
    base.mutual_aid_agreements_with_any_agencies = crmYesNo(extended.mutualAidAgreements);
    if (extended.withWhom.trim()) base.with_whom = extended.withWhom.trim();
  }

  putCrmString(base, "how_did_you_find_out_about_buxus", extended.howDidYouFindOutAboutBuxus);
  const source = extended.howDidYouFindOutAboutBuxus.trim();
  if (source === "Agency") {
    putCrmString(base, "how_did_you_find_out_about_buxus_agency", extended.howDidYouFindOutAboutBuxusAgency);
  }
  if (source === "Association") {
    putCrmString(
      base,
      "how_did_you_find_out_about_buxus_association",
      extended.howDidYouFindOutAboutBuxusAssociation,
    );
  }
  if (source === "Conference") {
    putCrmString(
      base,
      "how_did_you_find_out_about_buxus_conference",
      extended.howDidYouFindOutAboutBuxusConference,
    );
  }
  if (source === "Operator") {
    putCrmString(
      base,
      "how_did_you_find_out_about_buxus_operator",
      extended.howDidYouFindOutAboutBuxusOperator,
    );
  }
  if (source === "Training") {
    putCrmString(
      base,
      "how_did_you_find_out_about_buxus_trainer",
      extended.howDidYouFindOutAboutBuxusTrainer,
    );
  }
  if (source === "Webinar") {
    putCrmString(
      base,
      "how_did_you_find_out_about_buxus_webinar",
      extended.howDidYouFindOutAboutBuxusWebinar,
    );
  }

  return base;
}
